import { array } from '@ember/helper';
import { conversationMembersForDisplay } from '../utils/messaging/conversation';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Avatar from './avatar.gjs';
import Component from '@glimmer/component';
import FlexColumn from 'ember-simplepractice/components/spds/layout/flex/column';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import styles from './beginning-conversation-banner.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const LISTED_NAMES = 2;

export default class BeginningConversationBanner extends Component {
  @service intl;
  @service messaging;
  @service messagingNavigation;

  get conversation() {
    return this.args.conversation;
  }

  get profile() {
    return this.messaging.currentProfile;
  }

  get members() {
    return conversationMembersForDisplay(this.conversation, this.profile?.user).filter(
      member => member?.name
    );
  }

  get primaryRecipient() {
    return this.members[0];
  }

  get names() {
    const names = this.members.map(member => member.name);

    if (names.length > LISTED_NAMES) {
      const othersLabel = this.intl.t('emc.beginning_conversation_banner.others_count', {
        count: names.length - LISTED_NAMES,
      });
      names.splice(LISTED_NAMES, names.length, othersLabel);
    }

    return new Intl.ListFormat(this.intl.primaryLocale, {
      style: 'long',
      type: 'conjunction',
    })
      .formatToParts(names)
      .map(part =>
        part.type === 'literal' ? part.value.replace(/,\s*and\s+/g, ' and ') : part.value
      )
      .join('');
  }

  get isGroup() {
    return this.conversation.isGroup;
  }

  get isClinicianInGroup() {
    return Boolean(this.profile?.isClinician && this.isGroup);
  }

  get showClientWarning() {
    return this.isClinicianInGroup && this.conversation.containsClient;
  }

  get hasDisplayName() {
    return Boolean(this.conversation.displayName?.trim());
  }

  get showNameConversationAction() {
    return this.isClinicianInGroup;
  }

  get showAddTeamMembersAction() {
    return this.conversation.canAddTeamMembers(this.profile);
  }

  onNameConversation = () => {
    this.messagingNavigation.toConversationDetails();
  };

  onAddTeamMembers = () => {
    this.messagingNavigation.toAddMembers();
  };

  <template>
    <aside class={{styles.component}} data-test-beginning-conversation-banner>
      <FlexColumn @gap="1rem">
        <FlexColumn @alignItems="center" @gap="0.5rem" class="banner-header">
          {{#if this.isGroup}}
            {{svgJar
              "messaging-welcome"
              class="banner-illustration"
              aria-hidden="true"
              data-test-banner-illustration=true
            }}
          {{else if this.primaryRecipient}}
            <div class="banner-avatar" data-test-banner-avatar>
              <Avatar
                @users={{array this.primaryRecipient}}
                @isCouple={{this.conversation.isCouple}}
              />
            </div>
          {{/if}}

          <h2 class="banner-title" data-test-beginning-conversation-banner-title>
            {{#if this.names}}
              {{t "emc.beginning_conversation_banner.title" names=this.names}}
            {{else}}
              {{t "emc.beginning_conversation_banner.title_without_names"}}
            {{/if}}
          </h2>

          {{#if this.showClientWarning}}
            <p class="banner-description" data-test-beginning-conversation-banner-warning>
              {{t "emc.beginning_conversation_banner.client_warning"}}
            </p>
          {{/if}}
        </FlexColumn>

        {{#if (or this.showNameConversationAction this.showAddTeamMembersAction)}}
          <FlexColumn
            @alignItems="center"
            @gap="0.5rem"
            class="banner-actions"
            data-test-beginning-conversation-banner-actions
          >
            {{#if this.showNameConversationAction}}
              <GhostButton
                @size="small"
                class="banner-action"
                {{on "click" this.onNameConversation}}
                data-test-beginning-conversation-banner-name
              >
                {{svgJar "messaging-pen-write" class="banner-action-icon"}}
                {{#if this.hasDisplayName}}
                  {{t "emc.beginning_conversation_banner.rename_conversation"}}
                {{else}}
                  {{t "emc.beginning_conversation_banner.name_conversation"}}
                {{/if}}
              </GhostButton>
            {{/if}}

            {{#if this.showAddTeamMembersAction}}
              <GhostButton
                @size="small"
                class="banner-action"
                {{on "click" this.onAddTeamMembers}}
                data-test-beginning-conversation-banner-add-team-members
              >
                {{svgJar "messaging-user-plus" class="banner-action-icon"}}
                {{t "emc.beginning_conversation_banner.add_team_members"}}
              </GhostButton>
            {{/if}}
          </FlexColumn>
        {{/if}}
      </FlexColumn>
    </aside>
  </template>
}
